// ============================================
// Core Enums (Shared)
// ============================================

export enum QuestionnaireType {
  LIFESTYLE = "LIFESTYLE",
  HEALTH_HISTORY = "HEALTH_HISTORY",
  MENTAL_WELLBEING = "MENTAL_WELLBEING",
}

export enum QuestionType {
  INTENSE_EXERCISE = "INTENSE_EXERCISE",
  SLEEP = "SLEEP",
  FRUIT_VEG_CONSUMPTION = "FRUIT_VEG_CONSUMPTION",
  ALCOHOL_CONSUMPTION = "ALCOHOL_CONSUMPTION",
  SMOKER_STATUS = "SMOKER_STATUS",
  FAMILY_HISTORY = "FAMILY_HISTORY",
  FAMILY_TUBERCULOSIS = "FAMILY_TUBERCULOSIS",
  CONDITIONS = "CONDITIONS",
  HIV_TB = "HIV_TB",
  NERVOUS_OR_ON_EDGE = "NERVOUS_OR_ON_EDGE",
  UNCONTROLLABLE_WORRY = "UNCONTROLLABLE_WORRY",
  LOW_INTEREST_OR_PLEASURE = "LOW_INTEREST_OR_PLEASURE",
  FEELING_DOWN_OR_HOPELESS = "FEELING_DOWN_OR_HOPELESS",
}

export enum YesNoAnswer {
  YES = "YES",
  NO = "NO",
}

// ============================================
// Lifestyle Questionnaire Enums
// ============================================

export enum IntenseExerciseMinutes {
  ZERO_TO_150 = "0-150",
  ONE_FIFTY_TO_300 = "150-300",
  THREE_HUNDRED_PLUS = "300+",
}

export enum SleepHours {
  LESS_THAN_7 = "7",
  SEVEN_TO_9 = "7-9",
  MORE_THAN_9 = "9+",
}

export enum FruitVegServings {
  ONE = "1",
  TWO = "2",
  THREE = "3",
}

export enum AlcoholConsumption {
  NONE = "none",
  ONE = "1",
  TWO = "2",
  TWO_PLUS = "2+",
}

export enum SmokerStatus {
  NEVER = "never",
  STOPPED = "stopped",
  REGULAR = "regular",
}

// ============================================
// Health History Questionnaire Enums
// ============================================

export enum FamilyHistoryCondition {
  DIABETES = "DIABETES",
  HIGH_BLOOD_PRESSURE = "HIGH_BLOOD_PRESSURE",
  CORONARY_ARTERY_DISEASE = "CORONARY_ARTERY_DISEASE",
  NONE = "NONE",
}

export enum ConditionsCount {
  NONE = "NONE",
  ONE = "1",
  TWO_PLUS = "2+",
}

// ============================================
// Mental Wellbeing Questionnaire Enums
// ============================================

export enum MentalWellbeingFrequency {
  NOT_AT_ALL = "NOT_AT_ALL",
  SEVERAL_DAYS = "SEVERAL_DAYS",
  MORE_THAN_HALF = "MORE_THAN_HALF",
  NEARLY_EVERYDAY = "NEARLY_EVERYDAY",
}

// ============================================
// Interfaces
// ============================================

export interface QuestionOption {
  value: string;
  label?: string;
  translationKey?: string;
}

export interface QuestionMeta {
  values: string[];
  description?: boolean;
  performedStrengthExercises?: boolean | null;
}

export interface Question {
  type: string;
  question?: string;
  options?: QuestionOption[];
  meta?: QuestionMeta;
  multiSelect?: boolean;
}

export interface AnswerMeta {
  value: string | Record<string, boolean>;
  performedStrengthExercises?: boolean;
}

export interface QuestionnaireAnswer {
  type: string;
  meta: AnswerMeta;
}

/** Key-value map of answers by question type */
export interface QuestionnaireAnswers {
  [key: string]: QuestionnaireAnswer;
}

export interface HealthScoreQuestionnaireProps {
  /** Array of Question objects defining the questionnaire flow (order matters) */
  questions: Question[];
  /** i18n prefix for question/option translations (e.g., "Lifestyle.questionnaire") */
  translationPrefix: string;
  /** Callback invoked on final submission with answers as QuestionnaireAnswer[] array */
  onSubmit: (answers: QuestionnaireAnswer[]) => void | Promise<void>;
  /** Optional callback for close button; defaults to navigate(-1) if not provided */
  onClose?: () => void;
  /** Optional title for accessibility (used as aria-label on main container) */
  title?: string;
  /** Optional QuestionnaireAnswers object (key-value map) to pre-populate answers */
  initialAnswers?: QuestionnaireAnswers;
}
